import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { Loader2, X, Minus, GripHorizontal, Copy, Check, XCircle, ChevronDown } from 'lucide-react';
import CurrencyIcon from './CurrencyIcon';
import { RoleBadge } from './AcpRoleBadges';
import { MARKET_ACP_DOMAIN } from '../utils/markets';

/* ─── ACP Quick View — free-floating, draggable user-overview window.
   Mounted ONCE at App level (above the router's Routes, like
   MinimizedTicketDock) so it survives navigation between tool pages; it only
   renders on /tools routes. Every successful user search on a tools page
   (resolveAcpUser) broadcasts `clara:acp-quickview-open` and this window pops
   up with the ACP overview of that user — roles, VIP progress, Verification &
   Security, Account Info, balances — the ACPPanel summary, restyled in the
   Podium tools design. Dragging uses framer-motion (drag handle on the header,
   no momentum, constrained to the viewport); position persists in
   localStorage. Data is fetched through the same relay-query transport as the
   tool pages, on the market the search ran on (com / us). ─── */

export const ACP_QUICKVIEW_EVENT = 'clara:acp-quickview-open';

const API_URL = process.env.REACT_APP_API_URL;
const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const POS_KEY = 'acp-quickview-pos';
const SIZE_KEY = 'acp-quickview-size';
const QV_MARKETS = ['com', 'us'];
const FULL_W = 640;
const MIN_W = 400;
// Resize bounds — content needs ~480px before cells start wrapping badly.
const MIN_RESIZE_W = 480;
const MIN_RESIZE_H = 220;

async function acpRelayQuery(query, variables, market = 'com') {
  const token = localStorage.getItem('token');
  // 'X-Clara-Page': the Quick View floats over every tool page, so its calls
  // carry their own page tag instead of the underlying route — the tools-home
  // leaderboard excludes this page so Quick View traffic never counts toward
  // per-tool usage metrics (backend: /acp/leaderboard $ne filter).
  const { data } = await axios.post(`${API_URL}/api/qa/acp/relay-query`, { query, variables, market }, {
    headers: { Authorization: `Bearer ${token}`, 'X-Clara-Page': '/acp-quick-view' }, timeout: 30000,
  });
  return data;
}

/* Field-set verified against com AND us (2026-08-08/09, field-by-field via
   scripts/_probeUsAcpFields.js): none of these fields is market-blocked, so
   the user object never NULLs out. On us, kycStatus / flagProgress /
   transactionEligibilityState come back null (us uses Veriff instead of KYC
   levels) — every cell degrades individually. */
const GQL_QUICKVIEW = `query AcpQuickView($userId: String!) {
  info { currencies { name value } }
  user(userId: $userId) {
    id name email createdAt
    isBanned hasEmailVerified hasPhoneNumberVerified hasTfaEnabled hasEmailSubscribed oauthProvider
    phoneNumber phoneCountryCode
    kycStatus
    veriffStatus
    veriffUser { status reason internalStatus internalReason }
    veriffBiometricVerificationStatus
    veriffBypass { active createdAt auth { id name } }
    registeredWithVpn
    authorizedWithdrawalType
    paymentAccountType { accountType }
    userDepositHolding { amount currency }
    dailyTotalDeposited { dailyDepositedValue limit servertime }
    depositLimits { type limit currency progress expireAt }
    nationalId { nationalId }
    preference { defaultFiatCurrency }
    transactionEligibilityState { fiat { currency } }
    rakeback { rate enabled }
    flagProgress { progress flag }
    flags { flag }
    roles { name expireAt message }
    isMuted
    community { muteList(limit: 1) { message expireAt } }
    referredCampaign { name comission user { id name } }
    balances { available { amount currency } }
  }
}`;

/* ─── VIP mapping (same semantics as ACPPanel / BonusChecker) ─── */
const VIP_LEVEL_ORDER = [
  'none','bronze','silver','gold',
  'platinum-1','platinum-2','platinum-3','platinum-4','platinum-5','platinum-6',
  'diamond-1','diamond-2','diamond-3','diamond-4','diamond-5',
  'obsidian-1','obsidian-2','opal-1','opal-2','plutonium',
];
const VIP_LABEL_MAP = {
  none: 'None', bronze: 'Bronze', silver: 'Silver', gold: 'Gold',
  'platinum-1': 'Platinum I', 'platinum-2': 'Platinum II', 'platinum-3': 'Platinum III',
  'platinum-4': 'Platinum IV', 'platinum-5': 'Platinum V', 'platinum-6': 'Platinum VI',
  'diamond-1': 'Diamond I', 'diamond-2': 'Diamond II', 'diamond-3': 'Diamond III',
  'diamond-4': 'Diamond IV', 'diamond-5': 'Diamond V',
  'obsidian-1': 'Obsidian I', 'obsidian-2': 'Obsidian II',
  'opal-1': 'Opal I', 'opal-2': 'Opal II', plutonium: 'Plutonium',
};
const VIP_COLOR_MAP = {
  none: '#6b7280', bronze: '#b45309', silver: '#9ca3af', gold: '#eab308',
  platinum: '#22c55e', diamond: '#3b82f6', obsidian: '#7c3aed', opal: '#f59e0b', plutonium: '#f97316',
};

function parseWagerFlag(flag) {
  if (!flag) return null;
  const known = Object.keys(VIP_LABEL_MAP).filter((k) => k !== 'none');
  if (known.includes(flag)) return flag;
  const m = flag.match(/wagered\(([0-9.]+)(k|m|b)\)/i);
  if (!m) return null;
  const num = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  const amount = unit === 'k' ? num * 1e3 : unit === 'm' ? num * 1e6 : num * 1e9;
  const thresholds = [
    [25e9, 'plutonium'], [10e9, 'opal-2'], [5e9, 'opal-1'],
    [2.5e9, 'obsidian-2'], [1e9, 'obsidian-1'],
    [500e6, 'diamond-5'], [250e6, 'diamond-4'], [100e6, 'diamond-3'],
    [50e6, 'diamond-2'], [25e6, 'diamond-1'],
    [10e6, 'platinum-6'], [5e6, 'platinum-5'], [2.5e6, 'platinum-4'],
    [1e6, 'platinum-3'], [500e3, 'platinum-2'], [250e3, 'platinum-1'],
    [100e3, 'gold'], [50e3, 'silver'], [10e3, 'bronze'],
  ];
  for (const [t, level] of thresholds) if (amount >= t) return level;
  return null;
}

function deriveVip(u) {
  const fp = Array.isArray(u?.flagProgress) ? u.flagProgress[0] : u?.flagProgress;
  const fromProgress = parseWagerFlag(fp?.flag);
  if (fromProgress) return fromProgress;
  const earned = (u?.flags || []).map((f) => f.flag);
  const base = ['plutonium', 'opal', 'obsidian', 'diamond', 'platinum', 'gold', 'silver', 'bronze'];
  return base.find((t) => earned.includes(t)) || 'none';
}

/* KYC status → compact level label (same mapping as the ACP profile panel) */
function kycDisplay(kycStatus) {
  const s = kycStatus || '';
  const status = s.includes('Confirmed') ? 'Confirmed' : s.includes('Pending') ? 'Pending' : '';
  if (s.includes('ultimate')) return 'L4' + (status ? '-' + status : '');
  if (s.includes('full')) return 'L3' + (status ? '-' + status : '');
  if (s.includes('extended')) return 'L2' + (status ? '-' + status : '');
  if (s.includes('basic')) return 'L1' + (status ? '-' + status : '');
  return s || '—';
}

function fmtCreated(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

/* vipHosts / affiliateManagers name directories — one fetch per market per session */
const hostDirCache = {};
const mgrDirCache = {};
async function getDirectories(market) {
  if (!hostDirCache[market]) {
    hostDirCache[market] = acpRelayQuery('query VipHosts { vipHosts { id name } }', {}, market)
      .then((r) => r?.data?.vipHosts || []).catch(() => []);
    mgrDirCache[market] = acpRelayQuery('query AffiliateManagers { affiliateManagers { id name } }', {}, market)
      .then((r) => r?.data?.affiliateManagers || []).catch(() => []);
  }
  const [hosts, mgrs] = await Promise.all([hostDirCache[market], mgrDirCache[market]]);
  return { hosts, mgrs };
}

/* ─── Small building blocks (Podium) ─── */
function Cell({ label, tone, children }) {
  const toneCls = tone === 'green'
    ? 'inline-flex px-1.5 py-0.5 border border-[#007d48]/25 dark:border-[#1eaa52]/30 bg-[#007d48]/[0.06] dark:bg-[#1eaa52]/10 text-[#007d48] dark:text-[#1eaa52] font-bold'
    : tone === 'caution'
      ? 'inline-flex px-1.5 py-0.5 border border-amber-500/25 bg-amber-500/[0.08] text-amber-600 dark:text-amber-400 font-bold'
      : tone === 'red'
        ? 'inline-flex px-1.5 py-0.5 border border-[#d30005]/25 dark:border-[#ff5a4d]/30 bg-[#d30005]/[0.05] dark:bg-[#d30005]/10 text-[#d30005] dark:text-[#ff5a4d] font-bold'
        : 'text-[#111111] dark:text-white font-medium';
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#9e9ea0] dark:text-white/35 mb-1 whitespace-nowrap">{label}</div>
      <div className={`text-[11.5px] leading-tight break-words ${toneCls}`}>{children}</div>
    </div>
  );
}

/* ── PII masking ────────────────────────────────────────────────────────────
   Quick View is shown on every tool, so a customer's contact details would sit
   on screen all day. What support actually needs from these two fields is the
   STATUS — does a phone/email exist, and is it verified — not the value. So the
   value is masked and everything else is untouched: the same cells, the same
   verified/caution tones, and the same present-vs-absent text ('—' / 'No').

   Email keeps the domain, because that part is genuinely useful (provider,
   corporate vs free, typo'd TLDs) and is not personally identifying on its own.
   The local part becomes a fixed run of asterisks rather than one per character,
   so its length isn't leaked either. */
const MASK_RUN = '*****';

const maskEmail = (email) => {
  const s = String(email || '').trim();
  const at = s.lastIndexOf('@');
  // No '@' (or nothing before it) means this isn't shaped like an address —
  // mask the whole thing rather than risk echoing it back unmasked.
  if (at <= 0) return MASK_RUN;
  return `${MASK_RUN}${s.slice(at)}`;
};

// Every digit → '*', keeping '+' and the country/number spacing so the cell is
// still recognisably a phone number. Country code is masked too: it is part of
// the value, and the verified tone already conveys the status.
const maskPhone = (phone) => String(phone || '').replace(/\d/g, '*');

const fmtBalAmount = (n) => {
  const v = parseFloat(n);
  return v.toFixed(Math.abs(v) >= 1 ? 2 : 8);
};
const fmtUsd = (v) => (v >= 0.01 || v === 0 ? `$${v.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '<$0.01');

/* Balances: top-3 by USD value inline, the rest behind a "+N more" dropdown
   (vertical list with amount + ≈USD). Local open state — the component is
   keyed by user id, so the dropdown collapses on every new lookup. */
function BalancesRow({ balances, rates, fallbackCurrency }) {
  const [expanded, setExpanded] = useState(false);
  const ranked = balances
    .map((b) => ({ ...b, usd: (parseFloat(b.amount) || 0) * (rates?.[b.currency] ?? 0) }))
    .sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd));
  const top = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  const Pill = ({ b }) => (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#e5e5e5] dark:border-white/[0.1] bg-[#f5f5f5] dark:bg-white/[0.04] text-[11px] font-bold tabular-nums text-[#111111] dark:text-white"
      title={`≈ ${fmtUsd(Math.abs(b.usd))}`}
    >
      <CurrencyIcon currency={b.currency} size="sm" />
      {fmtBalAmount(b.amount)}
    </span>
  );

  return (
    <div className="border-t border-[#cacacb] dark:border-white/[0.12] pt-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#707072] dark:text-white/40">Balances</span>
        {ranked.length === 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#e5e5e5] dark:border-white/[0.1] bg-[#f5f5f5] dark:bg-white/[0.04] text-[11px] font-bold tabular-nums text-[#111111] dark:text-white">
            <CurrencyIcon currency={fallbackCurrency || 'btc'} size="sm" />0.00
          </span>
        )}
        {top.map((b, i) => <Pill key={i} b={b} />)}
        {rest.length > 0 && (
          <button
            onClick={() => setExpanded((x) => !x)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#cacacb] dark:border-white/[0.15] text-[10px] font-bold text-[#707072] dark:text-white/45 hover:text-[#111111] dark:hover:text-white hover:border-[#111111] dark:hover:border-white transition-colors"
          >
            {expanded ? 'Hide' : `+${rest.length} more`}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {expanded && rest.length > 0 && (
        <div className="mt-2 border border-[#e5e5e5] dark:border-white/[0.1] divide-y divide-[#f0f0f0] dark:divide-white/[0.06] max-h-[180px] overflow-y-auto scrollbar-overlay">
          {rest.map((b, i) => (
            <div key={i} className="flex items-center gap-2 px-2.5 py-1.5">
              <CurrencyIcon currency={b.currency} size="sm" />
              <span className="text-[10px] font-bold uppercase text-[#9e9ea0] dark:text-white/35 w-9">{b.currency}</span>
              <span className="text-[11px] font-bold tabular-nums text-[#111111] dark:text-white">{fmtBalAmount(b.amount)}</span>
              <span className="ml-auto text-[10px] tabular-nums text-[#9e9ea0] dark:text-white/35">≈ {fmtUsd(Math.abs(b.usd))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyMini({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="p-0.5 text-[#9e9ea0] dark:text-white/30 hover:text-[#111111] dark:hover:text-white transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-[#007d48] dark:text-[#1eaa52]" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function AcpQuickView() {
  const location = useLocation();
  const onTools = location.pathname.startsWith('/tools');

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [market, setMarket] = useState('com');
  const [target, setTarget] = useState(null);        // { id, name }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);            // { user, rates, hostName, hostStatus, managerName }
  const reqIdRef = useRef(0);
  // Live mirror of `open` for the event handler: a search must never expand a
  // minimized window, and a CLOSED window reappears minimized.
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);

  /* Position: left/top come from localStorage; framer-motion's drag transform
     accumulates on top while the window stays mounted. Leaving /tools unmounts
     the motion.div (transform resets), so on every hidden→visible transition
     the stored corner is re-read — the window reappears exactly where it was
     last dragged. onDragEnd stores the real on-screen corner. */
  const readStoredPos = () => {
    try {
      const p = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
      if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
        return {
          x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - 340)),
          y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - 160)),
        };
      }
    } catch { /* corrupt storage → default */ }
    return { x: Math.max(8, window.innerWidth - 670), y: 84 };
  };
  const [initialPos, setInitialPos] = useState(readStoredPos);

  /* Size: native CSS resize (bottom-right handle) on the expanded window; the
     user's size persists in localStorage. h === null means "auto height"
     (grow with content up to maxHeight) until the user grabs the handle. */
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(SIZE_KEY) || 'null');
      if (s && Number.isFinite(s.w)) {
        return {
          w: Math.max(MIN_RESIZE_W, Math.min(s.w, window.innerWidth - 24)),
          h: Number.isFinite(s.h) ? Math.max(MIN_RESIZE_H, Math.min(s.h, window.innerHeight - 24)) : null,
        };
      }
    } catch { /* corrupt storage → default */ }
    return { w: FULL_W, h: null };
  });
  const sizeRef = useRef(size);
  useEffect(() => { sizeRef.current = size; }, [size]);

  const visible = onTools && open;
  const prevVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) setInitialPos(readStoredPos());
    prevVisibleRef.current = visible;
  }, [visible]);

  /* Adopt + persist the size after a native CSS resize. There's no "resizeend"
     event, so on every pointerup compare the real box against the applied size
     — the browser writes inline width/height during a handle drag, which a
     React re-render would otherwise wipe (height is auto until first resize). */
  useEffect(() => {
    if (!visible || minimized) return;
    const onUp = () => {
      const el = windowRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cur = sizeRef.current;
      const wChanged = Math.abs(r.width - cur.w) > 2;
      const hChanged = cur.h ? Math.abs(r.height - cur.h) > 2 : /px/.test(el.style.height || '');
      if (!wChanged && !hChanged) return;
      const next = {
        w: Math.max(MIN_RESIZE_W, Math.round(r.width)),
        h: Math.max(MIN_RESIZE_H, Math.round(r.height)),
      };
      setSize(next);
      try { localStorage.setItem(SIZE_KEY, JSON.stringify(next)); } catch { /* quota */ }
    };
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, [visible, minimized]);

  const constraintsRef = useRef(null);
  const windowRef = useRef(null);
  const dragControls = useDragControls();
  const saveNewPos = useCallback(() => {
    const r = windowRef.current?.getBoundingClientRect();
    if (r) try { localStorage.setItem(POS_KEY, JSON.stringify({ x: r.left, y: r.top })); } catch { /* quota */ }
  }, []);

  /* Minimize / expand keeps the TOP-RIGHT corner in place (that's where the
     cursor is, on the header buttons) — the window grows/shrinks leftwards.
     The new left/top become the base position and the motion.div remounts
     (key flips) so the accumulated drag transform resets cleanly to 0. */
  const toggleMinimize = useCallback(() => {
    const r = windowRef.current?.getBoundingClientRect();
    const next = !minimized;
    if (r) {
      const newW = next ? MIN_W : sizeRef.current.w;
      const pos = {
        x: Math.max(8, Math.min(r.right - newW, window.innerWidth - 348)),
        y: Math.max(8, r.top),
      };
      setInitialPos(pos);
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* quota */ }
    }
    setMinimized(next);
  }, [minimized]);

  const fetchUser = useCallback(async (userId, mkt) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError('');
    try {
      const [main, vipRes, affRes, dirs] = await Promise.all([
        acpRelayQuery(GQL_QUICKVIEW, { userId }, mkt),
        acpRelayQuery('query LookupVipUserHost($userId: String!) { vipUserHost(userId: $userId) { hostId hostStatus } }', { userId }, mkt).catch(() => null),
        acpRelayQuery('query LookupAffiliateUser($userId: String!) { managedAffiliateUser(userId: $userId) { managerId } }', { userId }, mkt).catch(() => null),
        getDirectories(mkt),
      ]);
      if (reqId !== reqIdRef.current) return; // superseded by a newer search
      const user = main?.data?.user;
      if (!user) {
        const gqlErr = main?.errors?.[0]?.message;
        throw new Error(gqlErr || `User not found on stake.${mkt}`);
      }
      const hostId = vipRes?.data?.vipUserHost?.hostId;
      const managerId = affRes?.data?.managedAffiliateUser?.managerId;
      // currency → USD rate, for ranking balances by real value
      const rates = {};
      for (const c of main?.data?.info?.currencies || []) rates[c.name] = c.value;
      setData({
        user,
        rates,
        hostName: hostId ? (dirs.hosts.find((h) => h.id === hostId)?.name || hostId) : null,
        hostStatus: vipRes?.data?.vipUserHost?.hostStatus || null,
        managerName: managerId ? (dirs.mgrs.find((m) => m.id === managerId)?.name || managerId) : null,
      });
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setData(null);
      setError(err.response?.data?.error || err.message || 'Lookup failed');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  /* Tool-page searches land here. Minimize state is sticky: a minimized window
     stays minimized on new searches, and a closed one reappears MINIMIZED —
     the window never forces itself into your face. */
  useEffect(() => {
    const handler = (e) => {
      const { id, name, market: mkt } = e.detail || {};
      if (!id) return;
      // On the /acp workbench the full ACP panel IS the quick view: the page
      // consumes this same event and loads the user there (pages/acp), so the
      // floating window would only duplicate what is already on screen. The
      // same goes for a tool framed inside that page's dock (utils/dockEmbed):
      // its searches are forwarded to the parent's panel.
      if (window.location.pathname === '/acp' || window.location.pathname.startsWith('/acp/')) return;
      if (window.self !== window.top) return;
      const useMkt = QV_MARKETS.includes(mkt) ? mkt : 'com';
      setMarket(useMkt);
      setTarget({ id, name: name || null });
      if (!openRef.current) setMinimized(true);
      setOpen(true);
      fetchUser(id, useMkt);
    };
    window.addEventListener(ACP_QUICKVIEW_EVENT, handler);
    return () => window.removeEventListener(ACP_QUICKVIEW_EVENT, handler);
  }, [fetchUser]);

  /* Vertical wheel over the single-line roles row scrolls it horizontally.
     Native non-passive listener — React's onWheel can't preventDefault, and
     without it the window body would scroll instead. */
  const rolesRowRef = useRef(null);
  useEffect(() => {
    const el = rolesRowRef.current;
    if (!el) return;
    const onWheel = (ev) => {
      if (el.scrollWidth <= el.clientWidth) return;
      ev.preventDefault();
      el.scrollLeft += ev.deltaY + ev.deltaX;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [data, minimized]);

  if (!visible) return null;

  const u = data?.user;
  const vip = u ? deriveVip(u) : 'none';
  const fp = u ? (Array.isArray(u.flagProgress) ? u.flagProgress[0] : u.flagProgress) : null;
  const vipProgress = fp?.progress ?? 0;
  const vipColor = VIP_COLOR_MAP[vip.split('-')[0]] || '#6b7280';
  const curIdx = VIP_LEVEL_ORDER.indexOf(vip);
  const nextVip = curIdx >= 0 && curIdx < VIP_LEVEL_ORDER.length - 1 ? VIP_LABEL_MAP[VIP_LEVEL_ORDER[curIdx + 1]] : '';
  const phone = u?.phoneNumber ? `+${(u.phoneCountryCode || '').replace(/^\+/, '')} ${u.phoneNumber}` : '';
  const fiatElig = u?.transactionEligibilityState?.fiat || [];
  const prefFiat = fiatElig.length > 0 ? (fiatElig[0].currency || '').toUpperCase() : '—';
  // us ACP derives the red "Eligibility" cell from the kycBanned role + its message
  const kycBannedRole = (u?.roles || []).find((r) => /kycbanned/i.test(r.name || ''));
  // Deposit holdings arrive as one { amount, currency } (sweeps or gold)
  const holding = u?.userDepositHolding;
  const holdingFor = (cur) =>
    holding && holding.currency === cur && parseFloat(holding.amount) > 0 ? String(holding.amount) : '—';
  const dailySpend = u?.dailyTotalDeposited;
  const dailyReset = dailySpend?.servertime ? new Date(dailySpend.servertime) : null;
  const dailyResetIn = dailyReset && !isNaN(dailyReset) ? Math.max(0, Math.round((dailyReset.getTime() - Date.now()) / 3600000)) : null;
  // Fiat row of Daily Spend: the daily depositLimits entry (0/0 default when
  // absent — that's what the us ACP shows for accounts with no fiat limit).
  const fiatDailyLimit = (u?.depositLimits || []).find((l) => l.type === 'daily') || null;
  const nonZeroBalances = (u?.balances || [])
    .map((b) => b.available)
    .filter((a) => a && parseFloat(a.amount) !== 0);
  const mute = u?.community?.muteList?.[0];

  return createPortal(
    <div ref={constraintsRef} className="fixed inset-0 z-[80] pointer-events-none" style={{ fontFamily: FONT }}>
      <motion.div
        key={minimized ? 'min' : 'full'}
        ref={windowRef}
        drag
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={constraintsRef}
        dragMomentum={false}
        dragElastic={0}
        onDragEnd={saveNewPos}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
        className="pointer-events-auto absolute border border-[#cacacb] dark:border-white/[0.15] bg-white dark:bg-[#0A0A0B] shadow-[0_16px_48px_rgba(0,0,0,0.18)] dark:shadow-[0_16px_48px_rgba(0,0,0,0.65)] flex flex-col"
        style={{
          left: initialPos.x,
          top: initialPos.y,
          width: minimized ? MIN_W : size.w,
          height: !minimized && size.h ? size.h : undefined,
          maxHeight: '85vh',
          maxWidth: '92vw',
          minWidth: minimized ? undefined : MIN_RESIZE_W,
          minHeight: minimized ? undefined : MIN_RESIZE_H,
          // Native resize handle (bottom-right); minimized bar stays fixed-size
          resize: minimized ? 'none' : 'both',
          overflow: 'hidden',
        }}
      >
        {/* ── Drag-handle header ── */}
        <div
          onPointerDown={(e) => dragControls.start(e)}
          className="flex items-center gap-2 px-3 py-2 border-b border-[#cacacb] dark:border-white/[0.12] cursor-grab active:cursor-grabbing select-none shrink-0"
        >
          <GripHorizontal className="w-3.5 h-3.5 text-[#9e9ea0] dark:text-white/30 shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#111111] dark:text-white whitespace-nowrap shrink-0">ACP Quick View</span>
          {minimized && target && (
            <span className="text-[12px] font-bold text-[#111111] dark:text-white truncate min-w-0">
              · {u?.name || target.name || target.id.slice(0, 8)}
            </span>
          )}
          <span className="ml-auto" />
          <button
            onClick={toggleMinimize}
            className="p-1 text-[#9e9ea0] dark:text-white/35 hover:text-[#111111] dark:hover:text-white transition-colors"
            title={minimized ? 'Expand' : 'Minimize'}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-[#9e9ea0] dark:text-white/35 hover:text-[#d30005] dark:hover:text-[#ff5a4d] transition-colors"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {!minimized && (
          <>
            {/* ── Body ── */}
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-overlay px-3.5 py-3 space-y-3.5">
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 border border-[#d30005]/30 dark:border-[#d30005]/40 bg-[#d30005]/[0.04] dark:bg-[#d30005]/10">
                  <XCircle className="w-3 h-3 text-[#d30005] shrink-0" strokeWidth={2.25} />
                  <p className="text-[11.5px] font-medium text-[#d30005] dark:text-[#ff5a4d]">{error}</p>
                </div>
              )}

              {loading && !u && (
                <div className="flex items-center gap-2 py-4 text-[11.5px] text-[#707072] dark:text-white/45">
                  <Loader2 className="w-3 h-3 animate-spin text-[#111111] dark:text-white" />
                  Loading {target?.name || target?.id?.slice(0, 8) || 'user'}…
                </div>
              )}

              {!loading && !u && !error && (
                <p className="py-3 text-[11.5px] text-[#9e9ea0] dark:text-white/35">
                  Run a user search on any tool page — the overview shows up here.
                </p>
              )}

              {/* Stake.us data is in a verification phase — every displayed value
                  should be spot-checkable against the real ACP in one click. */}
              {market === 'us' && u && (
                <div className="flex items-start gap-2 px-3 py-2 border border-amber-500/30 bg-amber-500/[0.06] dark:bg-amber-500/10">
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 border border-amber-500/30 text-amber-600 dark:text-amber-400 shrink-0 mt-px">Testing</span>
                  <p className="text-[11px] leading-relaxed text-[#39393b] dark:text-white/70">
                    Stake.us view is in a testing phase — if any data looks wrong, reach out to{' '}
                    <span className="font-bold text-[#111111] dark:text-white">filipkozomara@mebit.io</span> on Slack.{' '}
                    <a
                      href={`https://${MARKET_ACP_DOMAIN.us}/users/${encodeURIComponent(u.id)}/overall`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-[#111111] dark:text-white underline underline-offset-2 decoration-amber-500/60 hover:decoration-[#111111] dark:hover:decoration-white whitespace-nowrap"
                    >
                      Verify in ACP ↗
                    </a>
                  </p>
                </div>
              )}

              {u && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={u.id + market}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.16 }}
                    className={`space-y-3.5 ${loading ? 'opacity-50' : ''}`}
                  >
                    {/* Identity: name + id, then roles in ONE scrollable row */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[15px] font-black uppercase tracking-[-0.02em] text-[#111111] dark:text-white truncate">{u.name}</span>
                      {u.isBanned && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] border border-[#d30005]/30 bg-[#d30005]/[0.06] text-[#d30005] dark:text-[#ff5a4d] shrink-0">Banned</span>
                      )}
                      <span className="inline-flex items-center gap-0.5 ml-auto min-w-0 shrink-0">
                        <span className="text-[9px] font-mono text-[#9e9ea0] dark:text-white/30 truncate max-w-[170px]">{u.id}</span>
                        <CopyMini text={u.id} />
                      </span>
                    </div>
                    {((u.roles || []).length > 0 || u.isMuted) && (
                      /* ACP-style letter badges in a single line — hover shows the
                         full role name + expiry + reason. Scrolls horizontally
                         (mouse wheel included) past the window edge if needed. */
                      <div ref={rolesRowRef} className="flex items-center gap-1.5 overflow-x-auto scrollbar-hairline pb-1 -mb-1">
                        {(u.roles || []).map((r, i) => <RoleBadge key={`${r.name}-${i}`} role={r} variant="letter" />)}
                        {u.isMuted && <RoleBadge role={{ name: 'Muted', expireAt: mute?.expireAt, message: mute?.message }} variant="letter" />}
                      </div>
                    )}

                    {/* VIP row */}
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.08em] border"
                        style={{ color: vipColor, borderColor: vipColor + '55', backgroundColor: vipColor + '14' }}
                      >
                        {VIP_LABEL_MAP[vip] || vip}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-[#f5f5f5] dark:bg-white/[0.08] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min(vipProgress * 100, 100)}%`, backgroundColor: vipColor }} />
                      </div>
                      <span className="text-[10px] font-bold tabular-nums" style={{ color: vipColor }}>{(vipProgress * 100).toFixed(1)}%</span>
                      {nextVip && <span className="text-[10px] text-[#9e9ea0] dark:text-white/35 whitespace-nowrap">→ {nextVip}</span>}
                      <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#9e9ea0] dark:text-white/30 border border-[#cacacb] dark:border-white/[0.15] px-1.5 py-0.5 rounded-full">stake.{market}</span>
                    </div>

                    {/* Verification & Security */}
                    <div className="border-t border-[#cacacb] dark:border-white/[0.12] pt-2.5">
                      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#707072] dark:text-white/40 mb-2">Verification &amp; Security</div>
                      <div className="flex flex-wrap gap-x-5 gap-y-2.5">
                        {/* com carries KYC levels; us runs on Veriff (kycStatus is null
                            there). The real us ACP's "Veriff" cell reads veriffUser (the
                            actual verification attempt), NOT veriffStatus — with a bypass,
                            veriffStatus flips to approved while veriffUser stays declined
                            (verified on LEO). No veriffUser + bypass → ACP leaves it empty. */}
                        {u.kycStatus ? (
                          <Cell label="KYC" tone={u.kycStatus.includes('Confirmed') ? 'green' : null}>{kycDisplay(u.kycStatus)}</Cell>
                        ) : u.veriffUser ? (
                          <Cell label="Veriff" tone={u.veriffUser.status === 'approved' ? 'green' : u.veriffUser.status === 'declined' ? 'red' : 'caution'}>
                            {u.veriffUser.status}{u.veriffUser.reason ? ` — ${u.veriffUser.reason}` : ''}
                          </Cell>
                        ) : u.veriffStatus && !u.veriffBypass?.active ? (
                          <Cell label="Veriff" tone={u.veriffStatus === 'approved' ? 'green' : u.veriffStatus === 'declined' ? 'red' : 'caution'}>{u.veriffStatus}</Cell>
                        ) : !u.veriffBypass?.active ? (
                          <Cell label="KYC">—</Cell>
                        ) : null}
                        {/* us ACP's Eligibility: kycBanned → "Declined — reason"; else from the
                            veriff attempt — approved→true, declined→false, expired→n/a
                            (verified on MAX / Joe / King / LEO / dan00 / FilipKo). */}
                        {kycBannedRole ? (
                          <Cell label="Eligibility" tone="red">Declined{kycBannedRole.message ? ` — ${kycBannedRole.message}` : ''}</Cell>
                        ) : u.veriffUser?.status === 'approved' ? (
                          <Cell label="Eligibility" tone="green">true</Cell>
                        ) : u.veriffUser?.status === 'declined' ? (
                          <Cell label="Eligibility" tone="red">false</Cell>
                        ) : u.veriffUser?.status === 'expired' ? (
                          <Cell label="Eligibility">n/a</Cell>
                        ) : null}
                        {u.veriffBiometricVerificationStatus && (
                          <Cell label="Veriff Biometric" tone="caution">{u.veriffBiometricVerificationStatus}</Cell>
                        )}
                        {u.veriffBypass?.active && (
                          <Cell label="Veriff Bypass" tone="green">
                            Enabled{u.veriffBypass.auth?.name ? ` — by ${u.veriffBypass.auth.name}` : ''}{u.veriffBypass.createdAt ? ` on ${fmtCreated(u.veriffBypass.createdAt)}` : ''}
                          </Cell>
                        )}
                        <Cell label={<>Email{u.email && u.hasEmailVerified ? <span className="text-[#007d48] dark:text-[#1eaa52] normal-case tracking-normal ml-0.5">verified</span> : null}</>} tone={u.email && u.hasEmailVerified ? 'green' : null}>{u.email ? maskEmail(u.email) : '—'}</Cell>
                        <Cell label="2FA" tone={u.hasTfaEnabled ? 'green' : null}>{u.hasTfaEnabled ? 'Yes' : 'No'}</Cell>
                        <Cell label="Phone" tone={phone ? (u.hasPhoneNumberVerified ? 'green' : 'caution') : null}>{phone ? maskPhone(phone) : 'No'}</Cell>
                        <Cell label="Account Type" tone="green">{u.oauthProvider || 'password'}</Cell>
                        {u.registeredWithVpn != null && (
                          <Cell label="VPN" tone={u.registeredWithVpn ? 'caution' : null}>{u.registeredWithVpn ? 'Yes' : 'No'}</Cell>
                        )}
                      </div>
                    </div>

                    {/* Account Info */}
                    <div className="border-t border-[#cacacb] dark:border-white/[0.12] pt-2.5">
                      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#707072] dark:text-white/40 mb-2">Account Info</div>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-x-4 gap-y-2.5">
                        <Cell label="Created">{fmtCreated(u.createdAt)}</Cell>
                        {/* Fiat currency is a com concept; the us ACP page shows payment
                            account type + crypto withdrawal status instead. */}
                        {market === 'us' ? (
                          <>
                            {/* null accountType renders as "Any" on the real us ACP (King) */}
                            <Cell label="Payment Acct Type">{u.paymentAccountType?.accountType || 'Any'}</Cell>
                            <Cell label="Crypto Withdrawal">{u.authorizedWithdrawalType || '—'}</Cell>
                          </>
                        ) : (
                          <Cell label="Fiat Currency">{prefFiat}</Cell>
                        )}
                        {/* us ACP treats a disabled rakeback as plain "No" */}
                        <Cell label="Rakeback" tone={u.rakeback?.enabled ? 'green' : market !== 'us' && u.rakeback?.rate > 0 ? 'caution' : null}>
                          {u.rakeback?.enabled
                            ? `${u.rakeback.rate} (on)`
                            : market === 'us' ? 'No' : u.rakeback?.rate > 0 ? `${u.rakeback.rate} (off)` : 'No'}
                        </Cell>
                        <Cell label="Referred" tone={u.referredCampaign ? 'green' : null}>
                          {u.referredCampaign
                            ? `${u.referredCampaign.user?.name || u.referredCampaign.name || '—'} | ${
                                market === 'us' ? Number(u.referredCampaign.comission ?? 0).toFixed(2) : (u.referredCampaign.comission ?? 0)
                              }`
                            : 'No'}
                        </Cell>
                        <Cell label="Subscribed" tone={u.hasEmailSubscribed ? 'green' : null}>{u.hasEmailSubscribed ? 'Yes' : 'No'}</Cell>
                        <Cell label="National ID">{u.nationalId?.nationalId || '—'}</Cell>
                        <Cell label="VIP Host" tone={data.hostName ? 'green' : null}>{data.hostName || 'none'}</Cell>
                        <Cell label="VIP Host Status">{data.hostStatus || (market === 'us' ? 'none' : '—')}</Cell>
                        <Cell label="Affiliate Manager" tone={data.managerName ? 'green' : null}>{data.managerName || 'none'}</Cell>
                        {market === 'us' && (
                          <>
                            <Cell label="Holding Sweeps">{holdingFor('sweeps')}</Cell>
                            <Cell label="Holding Gold">{holdingFor('gold')}</Cell>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Daily Spend — mirrors the us ACP block (crypto deposited vs daily limit + reset time) */}
                    {market === 'us' && dailySpend && (
                      <div className="border-t border-[#cacacb] dark:border-white/[0.12] pt-2.5 flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#707072] dark:text-white/40">Daily Spend</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#e5e5e5] dark:border-white/[0.1] bg-[#f5f5f5] dark:bg-white/[0.04] text-[11px] tabular-nums text-[#111111] dark:text-white">
                          <span className="text-[9px] font-bold uppercase text-[#9e9ea0] dark:text-white/35">Crypto</span>
                          <span className="font-bold">{Number(dailySpend.dailyDepositedValue ?? 0).toFixed(2)}</span>
                          <span className="text-[#9e9ea0] dark:text-white/35">/ {dailySpend.limit ?? '—'}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#e5e5e5] dark:border-white/[0.1] bg-[#f5f5f5] dark:bg-white/[0.04] text-[11px] tabular-nums text-[#111111] dark:text-white">
                          <span className="text-[9px] font-bold uppercase text-[#9e9ea0] dark:text-white/35">Fiat</span>
                          <span className="font-bold">{Number(fiatDailyLimit?.progress ?? 0).toFixed(2)}</span>
                          <span className="text-[#9e9ea0] dark:text-white/35">/ {fiatDailyLimit?.limit ?? 0}</span>
                        </span>
                        {dailyReset && !isNaN(dailyReset) && (
                          <span className="text-[10px] text-[#9e9ea0] dark:text-white/35">
                            Reset {dailyReset.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, month: 'numeric', day: 'numeric', year: 'numeric' })}
                            {dailyResetIn != null && ` — in ${dailyResetIn} hour${dailyResetIn === 1 ? '' : 's'}`}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Balances: top-3 by USD + dropdown for the rest */}
                    <BalancesRow
                      key={u.id + market}
                      balances={nonZeroBalances}
                      rates={data.rates}
                      fallbackCurrency={u.preference?.defaultFiatCurrency}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>,
    document.body
  );
}
